import cn from 'classnames';
import { FC, useCallback, useEffect, useRef } from 'react';
import { useIntl } from 'react-intl';

import { useAppDispatch, useAppSelector } from '@/shared/hooks';
import { Button, Modal, Select } from '@/shared/ui';

import { ModalID, modalModel } from '@/entities/modal';
import { TRANSCRIPTOR_LANGUAGE } from '@/entities/transcriptor';

import { transcriptorRecordingMessages } from '../../config/i18n';
import { useMicrophonePermissions, useStartRecording } from '../../hooks';

import styles from './ContinueRecordingModal.module.scss';

export const ContinueRecordingModal: FC = () => {
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();

  // Tolerate a missing modal entry: a freshly-added modal key can be absent
  // from an HMR-preserved store until a full reload, and we must not crash.
  const modalState = useAppSelector(
    (state) => state.modal[ModalID.TranscriptorContinueRecording],
  );
  const visible = modalState?.visible ?? false;
  const data = modalState?.data ?? { patientID: '', encounterID: '' };

  const cleanupAudioMonitoringRef = useRef<(() => void) | null>(null);

  const {
    hasPermission,
    isPermanentlyDenied,
    devices,
    selectedDevice,
    setSelectedDevice,
    audioLevel,
    startAudioLevelMonitoring,
    checkMicrophonePermission,
  } = useMicrophonePermissions();

  const selectMenuPortalTarget =
    typeof document !== 'undefined' ? document.body : null;

  const { startRecording } = useStartRecording({
    patientID: data.patientID,
    schemaId: data.schemaId,
    language: (data.language ?? '') as TRANSCRIPTOR_LANGUAGE,
    selectedDevice,
    devices,
    encounterId: data.encounterID,
    closeModalId: ModalID.TranscriptorContinueRecording,
    onCleanup: () => {
      if (cleanupAudioMonitoringRef.current) {
        cleanupAudioMonitoringRef.current();
        cleanupAudioMonitoringRef.current = null;
      }
    },
  });

  useEffect(() => {
    if (cleanupAudioMonitoringRef.current) {
      cleanupAudioMonitoringRef.current();
      cleanupAudioMonitoringRef.current = null;
    }
    if (!visible || !selectedDevice || hasPermission !== true) {
      return;
    }

    let active = true;
    startAudioLevelMonitoring(selectedDevice).then((cleanup) => {
      if (!cleanup) {
        return;
      }
      if (active) {
        cleanupAudioMonitoringRef.current = cleanup;
      } else {
        cleanup();
      }
    });

    return () => {
      active = false;
      if (cleanupAudioMonitoringRef.current) {
        cleanupAudioMonitoringRef.current();
        cleanupAudioMonitoringRef.current = null;
      }
    };
  }, [visible, selectedDevice, hasPermission, startAudioLevelMonitoring]);

  const handleClose = useCallback(() => {
    dispatch(
      modalModel.actions.closeModal(ModalID.TranscriptorContinueRecording),
    );
  }, [dispatch]);

  const handleStart = useCallback(async () => {
    if (hasPermission !== true) {
      await checkMicrophonePermission();
      return;
    }
    await startRecording();
  }, [hasPermission, checkMicrophonePermission, startRecording]);

  return (
    <Modal
      data-component="continue-recording-modal"
      isOpen={visible}
      onCancel={handleClose}
      title={formatMessage({
        id: 'transcriptor.continueRecordingModalTitle',
        defaultMessage: 'Continue recording',
      })}
      shouldRenderCancelButton={false}
      hideFooter
    >
      <div className={styles.content}>
        <h3 className={cn('h4', styles.title)}>
          {formatMessage(transcriptorRecordingMessages.microphoneConfigTitle)}
        </h3>

        {hasPermission === true && devices.length > 0 ? (
          <>
            <Select
              className={styles.select}
              isSearchable={false}
              value={selectedDevice}
              menuPortalTarget={selectMenuPortalTarget ?? undefined}
              menuPosition={selectMenuPortalTarget ? 'fixed' : undefined}
              onChange={(value) => {
                if (typeof value === 'string' || value === null) {
                  setSelectedDevice(value || '');
                }
              }}
              options={devices.map((device) => ({
                value: device.deviceId,
                label: device.label,
              }))}
              placeholder={formatMessage(
                transcriptorRecordingMessages.microphonePlaceholder,
              )}
            />

            {selectedDevice && (
              <div className={styles.audioLevelContainer}>
                <div className={cn('p3', styles.audioLevelLabel)}>
                  {formatMessage(transcriptorRecordingMessages.audioLevelLabel)}
                </div>
                <div className={styles.audioLevelBar}>
                  <div
                    className={styles.audioLevelFill}
                    style={{ width: `${Math.min(audioLevel, 100)}%` }}
                  />
                </div>
              </div>
            )}
          </>
        ) : (
          <p className={cn('p2', styles.description)}>
            {isPermanentlyDenied
              ? formatMessage({
                  id: 'transcriptor.microphoneDenied',
                  defaultMessage:
                    'Microphone access denied. Click the button below to request again or allow access in browser settings.',
                })
              : formatMessage({
                  id: 'transcriptor.requestMicrophoneAccess',
                  defaultMessage:
                    'Click the button below to request microphone access',
                })}
          </p>
        )}

        <div className={styles.actions}>
          <Button
            size="large"
            onClick={handleStart}
            disabled={hasPermission === true && !selectedDevice}
          >
            {hasPermission === true
              ? formatMessage(
                  transcriptorRecordingMessages.startRecordingButton,
                )
              : formatMessage({
                  id: 'transcriptor.requestMicrophoneButton',
                  defaultMessage: 'Request microphone access',
                })}
          </Button>
        </div>
      </div>
    </Modal>
  );
};
